import React, { useCallback, useEffect, useState } from 'react';
import { Brain, Check, X } from 'lucide-react';
import SectionShell from './SectionShell';
import { jobBriefApi } from '../../../services/jobBriefApi';

/**
 * HPToolsSection — recommendations refresh from `/hp-tools/recommend`
 * whenever `draft.error_traps` changes. Each recommendation is grounded
 * in the approved HP source or falls back to the "no approved source
 * connected" placeholder (never invents a citation).
 *
 * The user then acknowledges / assigns each tool. `hp_tools` on the draft
 * captures the acknowledgment status which feeds `computeHpReadiness`.
 */
export default function HPToolsSection({ draft, onChange }) {
  const acknowledgments = draft.hp_tools || [];
  const [recommendations, setRecommendations] = useState([]);
  const [fallback, setFallback] = useState('');
  const [loading, setLoading] = useState(false);

  const errorTrapsKey = JSON.stringify(draft.error_traps || []);

  useEffect(() => {
    let cancelled = false;
    if ((draft.error_traps || []).length === 0) {
      setRecommendations([]);
      return () => {};
    }
    setLoading(true);
    jobBriefApi.recommendHpTools(draft.error_traps || [])
      .then((d) => {
        if (cancelled) return;
        setRecommendations(d?.recommended || []);
        setFallback(d?.fallback_message || '');
      })
      .catch(() => { if (!cancelled) setRecommendations([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [errorTrapsKey]);

  const ackByToolId = React.useMemo(() => {
    const m = {};
    for (const t of acknowledgments) m[t.tool_id] = t;
    return m;
  }, [acknowledgments]);

  const acknowledge = useCallback((rec) => {
    const existing = ackByToolId[rec.tool_id];
    const nextRow = {
      tool_id:  rec.tool_id,
      label:    rec.label,
      topic:    rec.topic,
      when:     rec.when,
      status:   existing?.status === 'acknowledged' ? 'assigned' : 'acknowledged',
      citation: rec.citation || null,
    };
    const rest = acknowledgments.filter((t) => t.tool_id !== rec.tool_id);
    onChange({ hp_tools: [...rest, nextRow] });
  }, [acknowledgments, ackByToolId, onChange]);

  const decline = useCallback((tool_id) => {
    onChange({ hp_tools: acknowledgments.filter((t) => t.tool_id !== tool_id) });
  }, [acknowledgments, onChange]);

  const traps = draft.error_traps || [];

  return (
    <SectionShell
      icon={Brain}
      title="Human Performance Tools"
      subtitle="Recommendations refresh from the connected HP source whenever Error Traps change."
      color="#86efac"
      badge={acknowledgments.length ? `${acknowledgments.length} acknowledged` : 'Auto-recommends'}
      testId="jbv2-section-hp-tools"
    >
      {traps.length === 0 && (
        <div className="jbv2-empty" data-testid="jbv2-hp-empty-traps">
          Pick at least one Error Trap above to see the recommended HP tools for this job.
        </div>
      )}
      {traps.length > 0 && loading && (
        <div className="jbv2-empty">Fetching recommendations…</div>
      )}
      {traps.length > 0 && !loading && recommendations.length === 0 && (
        <div className="jbv2-empty" data-testid="jbv2-hp-no-recs">
          No HP tools match the selected traps yet. Try adding another trap that reflects the crew&rsquo;s risk.
        </div>
      )}
      {recommendations.map((rec, idx) => {
        const ack = ackByToolId[rec.tool_id];
        return (
          <div
            key={rec.tool_id}
            className="jbv2-row jbv2-hp-row"
            data-testid={`jbv2-hp-rec-${rec.tool_id}`}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', flexWrap: 'wrap' }}>
              <div className="jbv2-row-body" style={{ flex: 1 }}>
                <div className="jbv2-row-cat">{rec.label}</div>
                <div className="jbv2-hp-row-meta">
                  <strong>Use when:</strong> {rec.when}
                </div>
                <div className="jbv2-hp-row-meta" style={{ marginTop: 4 }}>
                  <strong>Addresses:</strong>{' '}
                  {(rec.matched_traps || []).map((t) => t.replaceAll('_', ' ')).join(', ')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  className="jbv2-row-btn"
                  onClick={() => acknowledge(rec)}
                  data-testid={`jbv2-hp-ack-${rec.tool_id}`}
                >
                  <Check size={12} /> {ack ? 'Toggle status' : 'Acknowledge'}
                </button>
                {ack && (
                  <button
                    type="button"
                    className="jbv2-row-btn jbv2-row-btn--danger"
                    onClick={() => decline(rec.tool_id)}
                    data-testid={`jbv2-hp-decline-${rec.tool_id}`}
                    aria-label="Remove acknowledgment"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {rec.citation ? (
                <span className="jbv2-hp-row-cite" data-testid={`jbv2-hp-cite-${rec.tool_id}`}>
                  Source: {rec.citation.source_title}
                </span>
              ) : (
                <span className="jbv2-hp-row-nosrc" data-testid={`jbv2-hp-nosrc-${rec.tool_id}`}>
                  No approved source connected — verify with supervisor
                </span>
              )}
              {ack && (
                <span
                  className="jbv2-hp-row-cite"
                  style={{ background: 'rgba(103,232,249,0.10)', borderColor: 'rgba(103,232,249,0.40)', color: '#67e8f9' }}
                  data-testid={`jbv2-hp-status-${rec.tool_id}`}
                >
                  Status: {ack.status}
                </span>
              )}
            </div>
          </div>
        );
      })}
      {fallback && recommendations.some((r) => !r.citation) && (
        <div className="jbv2-field-help" data-testid="jbv2-hp-fallback">{fallback}</div>
      )}
    </SectionShell>
  );
}
